/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { ConstraintAssignedIcon } from './ConstraintAssignedIcon';
import { TaskShadowMesh } from './TaskShadowMesh';
import { TicketCrewSize } from './TicketCrewSize';
import { TicketDateTextFontPath } from './TicketDateTextFontPath';
import { TicketDurationDays } from './TicketDurationDays';
import { TicketError } from './TicketError';
import { TicketIsLongest } from './TicketIsLongest';
import { TicketLocation } from './TicketLocation';
import { TicketLocationNameFontPath } from './TicketLocationNameFontPath';
import { TicketSelected } from './TicketSelected';
import { TicketSelectedTask } from './TicketSelectedTask';
import { TicketActivityNameFontPath } from './TicketActivityNameFontPath';
import { TicketActivityNameCanvas } from './TicketActivityNameCanvas';
import { TicketCompletedCheck } from './TicketCompletedCheck';
import { TicketDateTextCanvas } from './TicketDateTextCanvas';
import { TicketLocationNameCanvas } from './TicketLocationNameCanvas';
import { TicketWorkDaysContainer } from './TicketWorkDaysContainer';
import { TicketContainer } from '@appPlanApplication/tickets/TicketContainer';
import { IDynamicComponent } from '@appCanvasCommon/component-system/ComponentManager';
import { utils, Texture } from 'pixi.js-legacy';
import { TICKET_TEXTURE_OPTIONS } from '../tickets/TICKET_TEXTURE_OPTIONS';
import { planRendererInfo } from '@appCanvasCommon/core/RendererInfo';
import { CanvasAngularLand } from 'src/app/app-data/interfaces/canvas-angular-land';
import { Ticket } from '@touchplan/common';
import { cssVar } from '@touchplan/css-var';
import { TicketStarted } from './TicketStarted';
import { StickyContentContainer } from './TicketStickyContentContainer';

export class TicketSchema {
	constructor() {}

	public createCommonSchema(
		canvasAngularLand: CanvasAngularLand,
		ticketContainer: TicketContainer
	): IDynamicComponent {
		const common: IDynamicComponent = {};

		// Create always visible common components
		common['ALWAYS'] = [
			{
				creator: () =>
					new TicketCompletedCheck(
						utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_TICKET_COMPLETE_NAME],
						canvasAngularLand,
						ticketContainer
					),
				layer: 'SURFACE',
			},
		];

		common['MAXIMUM'] = [];

		// Common components which render when LOD is VERY_HIGH or higher
		common['VERY_HIGH'] = [];

		// Common components which render when LOD is COMFORTABLE or higher
		common['COMFORTABLE'] = [];

		// Common components which render when LOD is MEDIUM or higher
		common['MEDIUM'] = [
			// {
			// 	creator: () => new TicketBand((<any>Texture).TASK_TEXTURE, planState, ticketContainer),
			// 	layer: "ABOVE"
			// }
		];

		// WEBGL Specific
		if (planRendererInfo.isWebGL()) {
			common['MEDIUM'].push({
				creator: () => new TicketDateTextFontPath('Roboto Medium', canvasAngularLand, ticketContainer),
				layer: 'ABOVE',
			});
		} else {
			common['MEDIUM'].push({
				creator: () => new TicketDateTextCanvas(canvasAngularLand, ticketContainer),
				layer: 'ABOVE',
			});
		}

		// Common components which render when LOD is LOW or higher
		common['LOW'] = [];
		// WebGL specific components
		if (planRendererInfo.isWebGL()) {
			common['LOW'].push({
				creator: () => {
					const t = new TicketActivityNameFontPath('Roboto Medium', canvasAngularLand, ticketContainer);
					const stickyText = new StickyContentContainer(t, t.maskContainer);
					return stickyText;
				},
				layer: 'ABOVE',
			});
		} else {
			// Canvas specific components
			common['LOW'].push({
				creator: () => {
					const t = new TicketActivityNameCanvas(canvasAngularLand, ticketContainer);
					const stickyText = new StickyContentContainer(t, t.maskContainer);
					return stickyText;
				},
				layer: 'ABOVE',
			});
		}

		// Common components which render when LOD is VERY_LOW or higher
		common['VERY_LOW'] = [];

		return common;
	}

	public createGhostTaskSchema(canvasAngularLand: CanvasAngularLand, ticketContainer: TicketContainer) {
		const common: IDynamicComponent = {};
		const tr = ticketContainer.ticketManager.taskRenderer;
		const data: Ticket = ticketContainer.getData();
		const color =
			data.view.liveRole && data.view.liveRole.view.color ? data.view.liveRole.view.color : cssVar.black;
		const textCreator = this.getDescriptionTextCreator(canvasAngularLand, ticketContainer);
		// Create always visible common components
		common['ALWAYS'] = [
			{
				// TICKET IS SELECTED?
				creator: () =>
					new TicketSelectedTask(tr.getTexture('ticketOutlineMesh'), canvasAngularLand, ticketContainer),
				layer: 'BELOW',
			},
		];
		common['MAXIMUM'] = [];
		common['VERY_HIGH'] = [];
		common['COMFORTABLE'] = [];
		common['MEDIUM'] = [];
		common['LOW'] = [
			{
				creator: textCreator,
				layer: 'ABOVE',
			},
		];
		common['VERY_LOW'] = [];

		return common;
	}

	public createGhostMilestoneSchema(canvasAngularLand: CanvasAngularLand, ticketContainer: TicketContainer) {
		const common: IDynamicComponent = {};
		const data = ticketContainer.getData();
		const textCreator = this.getDescriptionTextCreator(canvasAngularLand, ticketContainer);
		// Create always visible common components
		common['ALWAYS'] = [
			{
				// TICKET IS SELECTED?
				creator: () =>
					new TicketSelected(
						utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_MILESTONE_SELECTED_NAME],
						utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_MILESTONE_SELECTED_PULL_NAME],
						canvasAngularLand,
						ticketContainer
					),
				layer: 'BELOW',
			},
		];
		common['MAXIMUM'] = [];
		common['VERY_HIGH'] = [];
		common['COMFORTABLE'] = [];
		common['MEDIUM'] = [];
		common['LOW'] = [
			{
				creator: textCreator,
				layer: 'ABOVE',
			},
		];
		common['VERY_LOW'] = [];

		return common;
	}

	public createGhostConstraintSchema(canvasAngularLand: CanvasAngularLand, ticketContainer: TicketContainer) {
		const common: IDynamicComponent = {};
		const data: Ticket = ticketContainer.getData();
		const color =
			data.view.liveRole && data.view.liveRole.view.color ? data.view.liveRole.view.color : cssVar.black;
		const textCreator = this.getDescriptionTextCreator(canvasAngularLand, ticketContainer);

		// Create always visible common components
		common['ALWAYS'] = [
			{
				// TICKET IS SELECTED?
				creator: () =>
					new TicketSelected(
						utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_CONSTRAINT_SELECTED_NAME],
						utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_CONSTRAINT_SELECTED_PULL_NAME],
						canvasAngularLand,
						ticketContainer
					),
				layer: 'BELOW',
			},
		];
		common['MAXIMUM'] = [];
		common['VERY_HIGH'] = [];
		common['COMFORTABLE'] = [];
		common['MEDIUM'] = [];
		common['LOW'] = [
			{
				creator: textCreator,
				layer: 'ABOVE',
			},
		];
		common['VERY_LOW'] = [];

		return common;
	}

	/**
	 * Davina said this is fine
	 * @param {PlanState} planState
	 * @param {TicketContainer} ticketContainer
	 */
	public createTaskSchema(
		canvasAngularLand: CanvasAngularLand,
		ticketContainer: TicketContainer
	): IDynamicComponent {
		const commonSchema = this.createCommonSchema(canvasAngularLand, ticketContainer);
		const tr = ticketContainer.ticketManager.taskRenderer;
		/**
		 * Davina: "You are doing great"
		 * TicketSelected draw order: (Higher in list = lower draw order)
		 *  - is selected
		 *  - is dependency focused
		 *  - is search result
		 * Also Davina: "no..."
		 */
		commonSchema['LOW'].push({
			creator: () => new TicketLocation((<any>Texture).TASK_TEXTURE, canvasAngularLand, ticketContainer),
			layer: 'ABOVE',
		});

		// WebGL specific components
		if (planRendererInfo.isWebGL()) {
			commonSchema['LOW'].push({
				creator: () => {
					const t = new TicketLocationNameFontPath('Roboto Medium', canvasAngularLand, ticketContainer);
					const stickyText = new StickyContentContainer(t, t.maskContainer);
					return stickyText;
				},
				layer: 'ABOVE',
			});
		} else {
			commonSchema['LOW'].push({
				creator: () => {
					const t = new TicketLocationNameCanvas(canvasAngularLand, ticketContainer);
					const stickyText = new StickyContentContainer(t, t.maskContainer);
					return stickyText;
				},
				layer: 'ABOVE',
			});
		}

		// Add the other low LOD components
		commonSchema['LOW'].push(
			{
				creator: () =>
					new TicketIsLongest(tr.getTexture('isLongestTexture'), canvasAngularLand, ticketContainer),
				layer: 'ABOVE',
			},
			{
				creator: () =>
					new TicketDurationDays(
						utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_DURATION_DAYS_NAME],
						canvasAngularLand,
						ticketContainer
					),
				layer: 'ABOVE',
			},
			{
				creator: () =>
					new TicketCrewSize(
						utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_CREW_ICON_NAME],
						canvasAngularLand,
						ticketContainer
					),
				layer: 'ABOVE',
			},
			{
				creator: () =>
					new TicketError(
						utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_ERROR_NAME],
						canvasAngularLand,
						ticketContainer
					),
				layer: 'ABOVE',
			}
		);

		commonSchema['MEDIUM'].push({
			creator: () =>
				new TaskShadowMesh(tr.getTexture('ticketShadowMesh'), canvasAngularLand, ticketContainer),
			layer: 'BELOW',
		});

		commonSchema['ALWAYS'].push(
			{
				// TICKET IS SELECTED?
				creator: () =>
					new TicketSelectedTask(tr.getTexture('ticketOutlineMesh'), canvasAngularLand, ticketContainer),
				layer: 'BELOW',
			},
			{
				creator: () =>
					new TicketWorkDaysContainer(
						utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_WORK_DAYS],
						utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_WORK_DAYS],
						canvasAngularLand,
						ticketContainer
					),
				layer: 'HIGHEST',
			},
			{
				creator: () => new TicketStarted(canvasAngularLand, ticketContainer),
				layer: 'ABOVE',
			}
		);

		return commonSchema;
	}

	public createMilestoneSchema(canvasAngularLand: CanvasAngularLand, ticketContainer: TicketContainer) {
		const commonSchema = this.createCommonSchema(canvasAngularLand, ticketContainer);

		commonSchema['ALWAYS'].push(
			// Is selected?
			{
				creator: () =>
					new TicketSelected(
						utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_MILESTONE_SELECTED_NAME],
						utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_MILESTONE_SELECTED_PULL_NAME],
						canvasAngularLand,
						ticketContainer
					),
				layer: 'BELOW',
			}
		);

		commonSchema['LOW'].push({
			creator: () =>
				new TicketError(
					utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_ERROR_NAME],
					canvasAngularLand,
					ticketContainer
				),
			layer: 'ABOVE',
		});

		return commonSchema;
	}

	public createConstraintSchema(canvasAngularLand: CanvasAngularLand, ticketContainer: TicketContainer) {
		const commonSchema = this.createCommonSchema(canvasAngularLand, ticketContainer);

		commonSchema['ALWAYS'].push(
			// IS SELECTED?
			{
				creator: () =>
					new TicketSelected(
						utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_CONSTRAINT_SELECTED_NAME],
						utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_CONSTRAINT_SELECTED_PULL_NAME],
						canvasAngularLand,
						ticketContainer
					),
				layer: 'BELOW',
			},
			// ASSIGNED PERSON
			{
				creator: () =>
					new ConstraintAssignedIcon(
						utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_CONSTRAINT_ASSIGNED_ICON],
						canvasAngularLand,
						ticketContainer
					),
				layer: 'ABOVE',
			}
		);

		commonSchema['LOW'].push({
			creator: () =>
				new TicketError(
					utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_ERROR_NAME],
					canvasAngularLand,
					ticketContainer
				),
			layer: 'ABOVE',
		});

		return commonSchema;
	}

	private getDescriptionTextCreator(canvasAngularLand: CanvasAngularLand, ticketContainer: TicketContainer) {
		if (planRendererInfo.isWebGL()) {
			return () => {
				const t = new TicketActivityNameFontPath('Roboto Medium', canvasAngularLand, ticketContainer);
				return t.maskContainer;
			};
		} else {
			return () => {
				const t = new TicketActivityNameCanvas(canvasAngularLand, ticketContainer);
				return t.maskContainer;
			};
		}
	}
}

export const ticketSchema = new TicketSchema();
